import React, { useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { Line, Station } from '@paragon/contract-library';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch, sortBy } from '@paragon/utilities';

import { sendAssemblyGroupToLines, sendAssemblyGroupToStations } from '../../actions/assemblyGroupActions';
import { useAppDispatch, useAppSelector } from '../hooks';
import { SendAssemblyGroupDialogProps } from './dialogs';

export function SendAssemblyGroupDialog({ assemblyGroupGuid }: SendAssemblyGroupDialogProps) {
  const dispatch = useAppDispatch();
  const stations = useAppSelector((state) => state.stations.stations);
  const regularStations = Object.values(stations).filter((station) => !station.followsSchedule);
  const sortedStations = sortBy(regularStations, (station) => station.name);

  const lines = useAppSelector((state) => state.lines.lines);
  const sortedLines = sortBy(Object.values(lines), (line) => line.name);

  const [stationOrLine, setStationOrLine] = useState<'station' | 'line'>('station');
  const [selectedStations, setSelectedStations] = useState<Station[]>([]);
  const [selectedLines, setSelectedLines] = useState<Line[]>([]);
  const [clearExistingComponents, setClearExistingComponents] = useState(true);

  function onSubmit() {
    dispatch(closeCurrentPopup());

    switch (stationOrLine) {
      case 'station':
        dispatch(sendAssemblyGroupToStations(assemblyGroupGuid, selectedStations, clearExistingComponents));
        break;
      case 'line':
        dispatch(sendAssemblyGroupToLines(assemblyGroupGuid, selectedLines, clearExistingComponents));
        break;
      default:
        assertExhaustiveSwitch(stationOrLine);
    }
  }

  return (
    <Dialog
      title="Send Assembly Group"
      submitText="Send"
      onSubmit={onSubmit}
      onCancel="close"
      submitDisabled={
        (stationOrLine === 'station' && selectedStations.length === 0) ||
        (stationOrLine === 'line' && selectedLines.length === 0)
      }
    >
      <Stack spacing={2}>
        <ToggleButtonGroup value={stationOrLine} onChange={(_, newValue) => setStationOrLine(newValue)} exclusive>
          <ToggleButton value="station">Station</ToggleButton>
          <ToggleButton value="line">Line</ToggleButton>
        </ToggleButtonGroup>

        {stationOrLine === 'station' && (
          <Autocomplete
            renderInput={(params) => <TextField {...params} variant="outlined" label="Select Station(s)" />}
            multiple
            property="Station"
            options={sortedStations}
            value={selectedStations}
            getOptionLabel={(station) => station.name}
            onChange={(_, stations) => void setSelectedStations(stations)}
          />
        )}
        {stationOrLine === 'line' && (
          <Autocomplete
            renderInput={(params) => <TextField {...params} variant="outlined" label="Select Line(s)" />}
            multiple
            property="Line"
            options={sortedLines}
            value={selectedLines}
            getOptionLabel={(line) => line.name}
            onChange={(_, lines) => void setSelectedLines(lines)}
          />
        )}

        <FormControlLabel
          label="Clear Existing Components"
          control={
            <Checkbox
              checked={clearExistingComponents}
              onChange={(event) => setClearExistingComponents(event.target.checked)}
            />
          }
        />
      </Stack>
    </Dialog>
  );
}
